import { useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import ReactDOM from "react-dom";
import {
  Input,
  Select,
  Row,
  Table,
  Col,
  Button,
  List,
  Typography,
  Dropdown,
  Menu,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowLeftOutlined,
  SearchOutlined,
  CloseOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { getSoaActivityLib } from "src/DigitalProtocolModule/utils/ajax-proxy";
import { ActivityLibraryType, SoaDetailsState } from "./types";
import "./index.scss";
import {
  compareMiddle,
  compareString,
  filterOptions,
} from "src/utils/common-function";
import { updateDPSoaActivities } from "src/DigitalProtocolModule/actions/createDP";
import { fetchHistory } from "src/actions/historyTrial";

export default function DpAddActivitySoa() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const state = location.state as SoaDetailsState;
  const trial_uuid = state.trial_id;

  const dpSoaState = useSelector(fetchHistory)?.data.dpSoaReducer;
  const {
    soaAddedLabs,
    soaAddedExamination,
    soaAddedProcedures,
    soaAddedQuestionnaires,
    soaAddedStudyProcedures,
  } = dpSoaState;

  const timer = useRef<any>();

  // the ids of status of history added activities
  const selectedActivityIds: number[] = [
    ...soaAddedLabs,
    ...soaAddedExamination,
    ...soaAddedProcedures,
    ...soaAddedQuestionnaires,
    ...soaAddedStudyProcedures,
  ].map((item) => item.activity_id);

  const [isLoading, setIsLoading] = useState(false);
  const [current, setCurrent] = useState(1);
  const [visibleValue, setVisibleValue] = useState(false);
  const [libraryCount, setLibraryCount] = useState<string>("");
  const [searchTxt, setSearchTxt] = useState("");
  // Original data from backend
  const [orgAll, setOrgAll] = useState<ActivityLibraryType[]>([]);
  // search bar dropdown data
  const [searchedActivity, setSearchedActivity] = useState<
    ActivityLibraryType[]
  >([]);
  // data list for table
  const [tableListItems, setTableListItems] = useState<ActivityLibraryType[]>(
    []
  );
  // selected activity list
  const [selectedActivityList, setSelectedActivityList] = useState<
    ActivityLibraryType[]
  >([]);

  const getSoaLibrary = async () => {
    try {
      setIsLoading(true);
      const res = await getSoaActivityLib(trial_uuid);
      if (res) {
        ReactDOM.unstable_batchedUpdates(() => {
          const temOrgAll: ActivityLibraryType[] = [
            ...res?.["Labs"],
            ...res?.["Physical Examination"],
            ...res?.["Procedures"],
            ...res?.["Questionnaires"],
            ...res?.["Study Procedures"],
          ];
          setOrgAll(temOrgAll);
          setLibraryCount(`${temOrgAll.length}`);
          setTableListItems(
            temOrgAll
              .filter((d) => {
                return d.historical_event === "YES";
              })
              .map((item) => {
                return {
                  ...item,
                  selected: selectedActivityIds.includes(item.activity_id),
                };
              })
          );
        });
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const navBack = () => {
    history.push({ pathname: "/dprotocol/soa-details", state });
  };

  const changePage = (page) => {
    setCurrent(page);
  };

  // on search input change
  const onTextChange = (e) => {
    const val = e.target.value.toLowerCase();
    let searchResult = [];
    !visibleValue && setVisibleValue(true);
    if (timer.current !== null) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(function () {
      setSearchTxt(val);
      searchResult =
        val.length > 0 ? filterOptions(orgAll, "Standard Activity", val) : [];
      setSearchedActivity(searchResult);
    }, 200);
  };

  // add to "Selected activities" by clicking on the activity item
  const toggleSelectOnClick = ({ key }) => {
    const targetActivityItem: ActivityLibraryType =
      typeof key === "string" ? JSON.parse(key) : key;
    if(targetActivityItem.selected) return
    const existedIndex = selectedActivityList.findIndex(
      (item) => item.activity_id === targetActivityItem.activity_id
    );
    if (existedIndex < 0) {
      setSelectedActivityList([...selectedActivityList, targetActivityItem]);
    } else {
      let tmpSelectedActivityList = [...selectedActivityList];
      tmpSelectedActivityList.splice(existedIndex, 1);
      setSelectedActivityList(tmpSelectedActivityList);
    }

    // update the select status in table
    const itemIndex = tableListItems.findIndex(
      (item) => item.activity_id === targetActivityItem.activity_id
    );
    if (itemIndex >= 0) {
      let tmpTableListItems = [...tableListItems];
      tmpTableListItems.splice(itemIndex, 1, {
        ...tableListItems[itemIndex],
        selected: existedIndex < 0,
      });
      setTableListItems(tmpTableListItems);
    }
  };

  const handleAddActivityToSoa = () => {
    if (selectedActivityList.length > 0) {
      dispatch(
        updateDPSoaActivities({
          ...dpSoaState,
          soaAddedLabs: [
            ...soaAddedLabs,
            ...selectedActivityList.filter((item) => item.Categories === 'Labs'),
          ].map((item, idx) => ({...item, activity_order_index: idx+1})),
          soaAddedExamination: [
            ...soaAddedExamination,
            ...selectedActivityList.filter(
              (item) => item.Categories === "Physical Examination"
            ),
          ].map((item, idx) => ({...item, activity_order_index: idx+1})),
          soaAddedProcedures: [
            ...soaAddedProcedures,
            ...selectedActivityList.filter(
              (item) => item.Categories === "Procedures"
            ),
          ].map((item, idx) => ({...item, activity_order_index: idx+1})),
          soaAddedQuestionnaires: [
            ...soaAddedQuestionnaires,
            ...selectedActivityList.filter(
              (item) => item.Categories === "Questionnaires"
            ),
          ].map((item, idx) => ({...item, activity_order_index: idx+1})),
          soaAddedStudyProcedures: [
            ...soaAddedStudyProcedures,
            ...selectedActivityList.filter(
              (item) => item.Categories === 'Study Procedures'
            ).map((item, idx) => ({...item, activity_order_index: idx+1})),
          ],
        })
      );
    }
    navBack();
  };

  const handleDiscard = () => {
    setSelectedActivityList([]);
    // reset select status in table
    setTableListItems(
      tableListItems.map((item) => ({
        ...item,
        selected: selectedActivityIds.includes(item.activity_id),
      }))
    );
    navBack();
  };

  const columns = [
    {
      title: "Activities",
      dataIndex: "Standard Activity",
      ellipsis: true,
      sorter: (a, b) =>
        a["Standard Activity"] < b["Standard Activity"] ? -1 : 1,
    },
    {
      width: "30%",
      title: "Category",
      dataIndex: "Categories",
      sorter: (a, b) => (a.Categories < b.Categories ? -1 : 1),
      ellipsis: true,
    },
    {
      width: 150,
      title: "",
      dataIndex: "operation",
      render: (_, record, index) => {
        return record.selected ? (
          <div style={{ float: "right", marginRight: 30 }}>
            <span style={{ paddingRight: "10px", fontSize: "16px" }}>
              {" "}
              <CheckOutlined />
            </span>
            Added
          </div>
        ) : (
          <div className="soa-lib" style={{ float: "right", marginRight: 30 }}>
            <Button
              onClick={(e) => {
                toggleSelectOnClick({ key: record });
              }}
              className="add-button"
            >
              Add
            </Button>
          </div>
        );
      },
    },
  ];

  const renderItem = (title: string, idx: any, Frequency: any) => {
    return (
      <div className="itemLine">
        <span
          className="itemTitle"
          style={{
            width: "80%",
            display: "inline-block",
            overflowWrap: "break-word",
            textOverflow: "ellipsis",
          }}
        >
          {searchTxt.length < title.length &&
            compareString(title, title.toLowerCase().split(searchTxt)[0])}
          <span
            className={`${
              searchTxt &&
              title.toLowerCase().indexOf(searchTxt.toLowerCase()) > -1
                ? "matched-item"
                : ""
            }`}
          >
            {compareMiddle(
              title,
              searchTxt.length - title.length === 0 ? title : searchTxt
            )}
          </span>
          {searchTxt.length !== 0 &&
            searchTxt.length < title.length &&
            compareMiddle(
              title,
              title
                .toLowerCase()
                .slice(
                  (
                    title.toLowerCase().split(searchTxt)[0] +
                    (searchTxt.length - title.length === 0
                      ? title
                      : searchTxt) +
                    ""
                  ).length
                )
            )}
        </span>
        <span
          style={{
            width: "20%",
            display: "inline-block",
            color: "#CA4A04",
            marginLeft: "25px",
          }}
        >
          Add
        </span>
      </div>
    );
  };

  const renderItemClick = (title: string, idx: any, Frequency: any) => {
    return (
      <div className="itemLine">
        <span
          className="itemTitle"
          style={{
            width: "80%",
            display: "inline-block",
            overflowWrap: "break-word",
            textOverflow: "ellipsis",
          }}
        >
          {searchTxt.length < title.length &&
            compareString(title, title.toLowerCase().split(searchTxt)[0])}
          <span
            className={`${
              searchTxt &&
              title.toLowerCase().indexOf(searchTxt.toLowerCase()) > -1
                ? "matched-item"
                : ""
            }`}
          >
            {compareMiddle(
              title,
              searchTxt.length - title.length === 0 ? title : searchTxt
            )}
          </span>
          {searchTxt.length !== 0 &&
            searchTxt.length < title.length &&
            compareMiddle(
              title,
              title
                .toLowerCase()
                .slice(
                  (
                    title.toLowerCase().split(searchTxt)[0] +
                    (searchTxt.length - title.length === 0
                      ? title
                      : searchTxt) +
                    ""
                  ).length
                )
            )}
        </span>
        <span
          style={{
            width: "20%",
            display: "inline-block",
            fontWeight: 700,
            marginLeft: "25px",
          }}
        >
          <CheckOutlined />
        </span>
      </div>
    );
  };

  const menu = (
    <Menu onClick={toggleSelectOnClick}>
      {searchTxt.length !== 0 && searchedActivity.length > 0 && (
        <Menu.ItemGroup title="">
          {searchedActivity.slice(0, 10).map((item, idx) => {
            if (
              [
                ...soaAddedLabs,
                ...soaAddedExamination,
                ...soaAddedProcedures,
                ...soaAddedQuestionnaires,
                ...soaAddedStudyProcedures,
                ...selectedActivityList,
              ].findIndex(
                (domain) =>
                  item["Standard Activity"] == domain["Standard Activity"]
              ) < 0
            ) {
              return (
                <Menu.Item key={JSON.stringify({ ...item, selected: false })}>
                  {renderItem(item["Standard Activity"], idx, item.Frequency)}
                </Menu.Item>
              );
            } else {
              return (
                <Menu.Item key={JSON.stringify({ ...item, selected: true })}>
                  {renderItemClick(
                    item["Standard Activity"],
                    idx,
                    item.Frequency
                  )}
                </Menu.Item>
              );
            }
          })}
        </Menu.ItemGroup>
      )}
      {/* Show blank when not searching */}
      {searchTxt.length === 0 && (
        <div style={{ cursor: "none", padding: 20 }}></div>
      )}
      {/* Show info when no results */}
      {searchTxt.length !== 0 && searchedActivity.length === 0 && (
        <div key="no-result" style={{ cursor: "none", padding: 20 }}>
          No results match your search. Try a different keyword.
        </div>
      )}
    </Menu>
  );

  useEffect(() => {
    getSoaLibrary();
  }, []);

  return (
    <div className="dp-add-activity-soa-root">
      <div className="herder">
        <div onClick={handleAddActivityToSoa} className="back-btn">
          <ArrowLeftOutlined />
          <span className="label">Back</span>
        </div>
        <div>
          <div className="page-title">Activities Library</div>
        </div>
      </div>

      <div className="body">
        <Row>
          <Col span={18}>
            <div className="search-bar">
              <div>
                <Dropdown
                  overlay={menu}
                  overlayClassName="searchbox"
                  className="search-soa"
                  visible={visibleValue}
                  onVisibleChange={(visible: boolean) => {
                    !visibleValue
                      ? setVisibleValue(true)
                      : setVisibleValue(false);
                  }}
                >
                  <Input
                    prefix={<SearchOutlined />}
                    className="search-input"
                    style={{ width: "100%", height: 37 }}
                    allowClear
                    onChange={onTextChange}
                    onClick={(e) => e.preventDefault()}
                    placeholder="Search by keyword"
                    maxLength={100}
                  />
                </Dropdown>
              </div>
            </div>

            <div className="result-table">
              <Table
                columns={columns}
                dataSource={tableListItems}
                loading={isLoading}
                pagination={{
                  position: ["bottomRight"],
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${tableListItems.length} items`,
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  onChange: changePage,
                  current: current,
                  total: tableListItems.length,
                }}
              />
            </div>
          </Col>
          <Col span={6}>
            <div className="activities-container">
              <div className="side-panel-heading">Selected activities</div>
              <List
                className="lists-container"
                bordered
                dataSource={selectedActivityList}
                renderItem={(item) => (
                  <List.Item>
                    <Typography.Text>
                      <span className="category">{`${item["Standard Activity"]}`}</span>
                      <span className="sub-category">{`in ${item["Categories"]}`}</span>{" "}
                    </Typography.Text>
                    <span
                      className="del-icon"
                      onClick={() => toggleSelectOnClick({ key: item })}
                    >
                      <CloseOutlined />
                    </span>
                  </List.Item>
                )}
              />

              <div className="soa-activity-btn-container">
                <Button onClick={handleAddActivityToSoa} className="add-soa">
                  Add to SoA
                </Button>
                <Button
                  block
                  onClick={handleDiscard}
                  disabled={selectedActivityList.length === 0}
                  className="del-soa"
                >
                  Discard
                </Button>
                <br />
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
}
